/**
 * FO6 Structure Loss Settlement and Roof Loss Settlement Business Logic Utilities
 *
 * This file contains all business logic for FO-6 Structures Loss Settlement options
 * and Roof Loss Settlement options, including structure category constraints and
 * access control (both role-based and age-based).
 *
 * Business Rules Implementation:
 *
 * Structure Category Rules:
 * - (a) Confinement Buildings: RC and ACV options for both Structure and Roof Loss Settlement
 * - (b) Dwelling and Contents: ACV only for both
 * - (c) Fencing: ACV only for Structure Loss Settlement, no Roof Loss Settlement field
 * - (d) Hoop Buildings: ACV only for both
 * - (e) Poultry Buildings: Age-based RC (same as confinement), role-based fallback for older buildings
 * - (f) Silos and Grain Bins: underwriters see RC options, everyone else sees ACV only
 * - (g) Barns/Outbuildings: Building class logic (1/2=RC, 3=ACV)
 *
 * Role-Based Access Control:
 * - Poultry and Silos/Grain Bins: Agents get ACV only, Underwriters get both ACV and RC
 *
 * Age-Based Access Control:
 * - Confinement Buildings: RC available when building age ≤ configured threshold (default 15 years)
 *   - Available to all agents (independent and staff) when age criteria are met
 *   - Buildings older than threshold or with unknown age: ACV only
 * - Poultry Buildings: Same age-based logic as confinement buildings
 *   - Buildings ≤ threshold: RC available to all agents
 *   - Buildings older than threshold or with unknown age: role-based fallback (underwriter-only RC)
 *
 * Interdependency Rules:
 * - When Structure Loss Settlement is ACV, Roof Loss Settlement must also be ACV
 * - When Structure Loss Settlement is RC, Roof Loss Settlement can be ACV or RC
 *
 * Related GitHub Issues:
 * - Original implementation: #28408
 * - Structure category validation: #28408v2
 * - Age-based RC for confinement buildings: #28945
 *
 * @file FO6.utils.js
 * @version 2.0
 */

import { holdsLegacyFo6RoofShape } from '@ourbranch/lookups';
import { isRcWithoutRceApproved } from '@ourbranch/farm-utils';
import { COVERAGE_LIMIT_OVERRIDE_FLOOR, COVERAGE_LIMIT_OVERRIDE_HELP_TEXT } from 'common/helpers/yup-helpers';

// #31836: minimum Coverage E an underwriter may set on FO6 ACV items, overriding the
// 80% ACV floor. Mirrors UW_ACV_COVERAGE_E_FLOOR in common/helpers/yup-helpers.js (the
// Yup submission path) so inline and submit validation agree.
const UW_ACV_COVERAGE_E_FLOOR = 1000;

/**
 * #35868: an authorized Coverage E limit override (limitOverride, #35617) bypasses the 80% RC/ACV
 * floors and the cap, enforcing only the $1,000 hard floor — exactly what the Yup submission path
 * (rc80PercentRequirement in yup-helpers.js) accepts. Both inline validators defer to this first so
 * the field never shows a red "must be at least 80%" error for a limit the form will save, and the
 * helper text states the override rules instead of the standard range. Returns null when the risk is
 * not overridden.
 */
const validateOverriddenCoverageE = (coverages) => {
  if (!coverages?.limitOverride) {
    return null;
  }
  if ((coverages.coverageE || 0) < COVERAGE_LIMIT_OVERRIDE_FLOOR) {
    return {
      isValid: false,
      message: `Coverage E must be at least $${COVERAGE_LIMIT_OVERRIDE_FLOOR.toLocaleString()}.`
    };
  }
  return { isValid: true, message: COVERAGE_LIMIT_OVERRIDE_HELP_TEXT };
};

/**
 * Validates 80% replacement cost requirement for RC selections
 * @param {Object} params - Parameters object
 * @param {Object} params.coverages - Coverage data
 * @param {Object} params.building - Building data with replacementCostEstimate
 * @param {boolean} [params.isNewRisk=false] - Whether this is a new (unrated) risk
 * @param {boolean} [params.isMigratedWithoutRce=false] - #37217: the structure came over in a migration whose
 *   structures carry no RCE (Forreston), so RC settlement needs none
 * @returns {Object} Validation result with isValid and message
 */
export const validate80PercentRC = ({ coverages, building, isNewRisk = false, isMigratedWithoutRce = false }) => {
  // #35868: an authorized limit override is validated against the $1,000 floor only.
  const overridden = validateOverriddenCoverageE(coverages);
  if (overridden) {
    return overridden;
  }

  // NONE (roof excluded) doesn't require 80% RC validation
  if (coverages?.roofLossSettlement === 'NONE') {
    return { isValid: true, message: '' };
  }

  if (
    coverages?.structureLossSettlement === 'RC' ||
    coverages?.roofLossSettlement === 'RC' ||
    coverages?.roofLossSettlement === 'RC_ACV_WIND_HAIL'
  ) {
    const actualReplacementCost = building?.replacementCostEstimate?.total || 0;

    // For new risks, allow RC selection without RCE validation
    if (isNewRisk && actualReplacementCost === 0) {
      return {
        isValid: true,
        message: '✓ RC loss settlement selected. Validation will apply after rating.'
      };
    }

    // Existing validation logic for rated risks
    if (actualReplacementCost > 0) {
      const coverageE = coverages?.coverageE || 0;
      const minimumRequired = actualReplacementCost * 0.8;

      if (coverageE < minimumRequired) {
        return {
          isValid: false,
          message: `Coverage E must be at least 80% of replacement cost ($${minimumRequired.toLocaleString()}) when RC is selected. Current RCE: $${actualReplacementCost.toLocaleString()}.`
        };
      }

      return {
        isValid: true,
        message: `✓ Coverage E meets 80% RC requirement ($${minimumRequired.toLocaleString()} minimum).`
      };
    }

    // #37093: the CFM GO import approved this structure's RC settlement without an RCE.
    if (isRcWithoutRceApproved({ coverages, building })) {
      return {
        isValid: true,
        message: 'RC loss settlement approved without an RCE (grandfathered from GO).'
      };
    }

    // #37217: a migrated structure keeps its RC settlement without an RCE.
    if (isMigratedWithoutRce) {
      return { isValid: true, message: '' };
    }

    // Only show error for existing risks without RCE data
    return {
      isValid: false,
      message: 'RC loss settlement selected but RCE data not available for validation.'
    };
  }

  return { isValid: true, message: '' };
};

/**
 * Validates 80% actual cash value requirement for ACV loss settlement selections.
 * For ACV buildings, Coverage E must be at least 80% of ACV.
 * Related issues: #28950, #28923
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.coverages - Coverage data
 * @param {Object} params.building - Building data with actualCashValueCalculation
 * @param {boolean} [params.isNewRisk=false] - Whether this is a new (unrated) risk
 * @returns {Object} Validation result with isValid and message
 */
export const validate80PercentACV = ({ coverages, building, isNewRisk = false, isUnderwriter: isUW = false }) => {
  if (coverages?.structureLossSettlement !== 'ACV') {
    return { isValid: true, message: '' };
  }

  // #35868: an authorized limit override is validated against the $1,000 floor only.
  const overridden = validateOverriddenCoverageE(coverages);
  if (overridden) {
    return overridden;
  }

  const acvTotal = building?.actualCashValueCalculation?.total || 0;

  // For new risks without ACV data, allow selection without validation
  if (isNewRisk && acvTotal === 0) {
    return { isValid: true, message: '' };
  }

  if (acvTotal > 0) {
    const coverageE = coverages?.coverageE || 0;
    const eightyPercentAcv = Math.round(acvTotal * 0.8);
    // #31836: underwriters may lower FO6 ACV Coverage E to a $1,000 floor; agents are
    // held to 80% of ACV. Keep this inline validation in step with the Yup submission
    // floor (rc80PercentRequirement) so the field doesn't show a red error for UW — and,
    // once a UW has approved a sub-floor value (acvFloorApprovedByUW), for any later viewer.
    const uwApprovedAcvFloor = Boolean(coverages?.acvFloorApprovedByUW);
    const minimumRequired =
      isUW || uwApprovedAcvFloor ? Math.min(UW_ACV_COVERAGE_E_FLOOR, eightyPercentAcv) : eightyPercentAcv;

    if (coverageE < minimumRequired) {
      return {
        isValid: false,
        message: isUW
          ? `Coverage E must be at least $${minimumRequired.toLocaleString()}.`
          : `Coverage E must be at least 80% of ACV ($${minimumRequired.toLocaleString()}). Current ACV: $${acvTotal.toLocaleString()}.`
      };
    }

    return {
      isValid: true,
      message: `Coverage E meets 80% ACV requirement ($${minimumRequired.toLocaleString()} minimum).`
    };
  }

  return { isValid: true, message: '' };
};

/**
 * #31836: the normal (agent) Coverage E floor for an FO6 ACV item — 80% of its ACV total when one
 * exists, otherwise 80% of its RCE total (fencing carries no ACV calculation). Returns null when
 * neither basis is available yet. Used to decide when an underwriter has lowered Coverage E below
 * the floor and the acvFloorApprovedByUW marker should be persisted.
 *
 * @param {Object} building - The FO6 structure's building data.
 * @returns {number|null} The 80% floor, or null when no ACV/RCE basis is available.
 */
export const getFo6AcvItemFloor = (building) => {
  const acvTotal = building?.actualCashValueCalculation?.total;
  if (acvTotal && acvTotal > 0) {
    return Math.round(acvTotal * 0.8);
  }
  const rceTotal = building?.replacementCostEstimate?.total;
  if (rceTotal && rceTotal > 0) {
    return Math.round(rceTotal * 0.8);
  }
  return null;
};

/**
 * Role-based access control helper - checks if user is agent
 * @param {Object} session - User session object
 * @returns {boolean} Whether user has agent role
 */
/**
 * Gets role-based loss settlement options for structure types requiring role-based access control
 * @param {string} structureType - Type of structure ('storageGrain', 'confinement', 'poultry')
 * @param {Object} session - User session object
 * @returns {Array} Array of loss settlement options based on user role
 */
export const getRoleBasedLossSettlementOptions = (structureType, session) => {
  const acvOnly = [{ id: 'ACV', value: 'Actual cash value' }];
  const isUw = Boolean(session?.canApproveUWReview);

  // #33612: Confinement & Poultry — Replacement Cost requires UW approval, but the AGENT may now
  // SELECT it. Agents (and any non-UW / unknown session) see "Replacement cost (Requires UW Approval)"
  // and can pick it; the backend post-data-rules downgrade it back to ACV unless a UW rates the offer
  // (mirrors the FO6 roof-RC gate). The underwriter — being the approver — sees plain "Replacement
  // cost" with no approval wording.
  if (structureType === 'confinement' || structureType === 'poultry') {
    return [
      { id: 'ACV', value: 'Actual cash value' },
      { id: 'RC', value: isUw ? 'Replacement cost' : 'Replacement cost (Requires UW Approval)' }
    ];
  }

  // Silos/Grain Bins (storageGrain): agents ACV only, underwriters ACV + RC.
  if (!session) {
    return acvOnly;
  }
  if (isAgent(session)) {
    return acvOnly;
  }
  if (isUw) {
    return [
      { id: 'ACV', value: 'Actual cash value' },
      { id: 'RC', value: 'Replacement cost' }
    ];
  }

  // Fallback for other roles (admins, super-users, etc.) — most restrictive for safety.
  return acvOnly;
};

/**
 * Gets loss settlement options based on structure type, barn type, and user session
 *
 * This is the main entry point for structure loss settlement options, implementing
 * all business rules for different structure categories and role-based access control.
 *
 * Structure Category Business Rules:
 * - Dwelling/Contents, Hoop Buildings, Fencing: Always ACV only (no RC allowed)
 * - Silos/Grain Bins: Role-based (Agents get ACV only, Underwriters get both)
 * - Confinement Buildings: Age-based (buildings ≤ maxAgeThreshold get both ACV and RC, older buildings get ACV only)
 * - Poultry Buildings: Age-based (buildings ≤ maxAgeThreshold get both ACV and RC), role-based fallback for older
 * - Barns/Outbuildings: Building class determines options (1/2=RC only, 3=ACV only)
 *
 * @param {Object} params - Parameters object
 * @param {boolean} params.isDwellingContents - Whether structure is dwelling/contents
 * @param {boolean} params.isHoopBuilding - Whether structure is hoop building
 * @param {boolean} params.isFencing - Whether structure is fencing
 * @param {boolean} params.isStorageGrain - Whether structure is storage/grain (Silos/Grain Bins)
 * @param {boolean} params.isConfinementBuilding - Whether structure is confinement building
 * @param {boolean} params.isPoultryBuilding - Whether structure is poultry building
 * @param {boolean} params.isBarnOutbuilding - Whether structure is barn/outbuilding
 * @param {number|null} params.buildingClass - Building class (1, 2, 3) for barn/outbuilding structures
 * @param {Object} params.session - User session object containing role information
 * @param {number|null} params.buildingAge - Age of the building in years (for confinement and poultry buildings)
 * @param {number} params.maxAgeThreshold - Maximum age threshold for RC eligibility (for confinement and poultry buildings)
 * @returns {Array} Array of loss settlement options with id/value pairs
 */
export const getLossSettlementOptions = ({
  isDwellingContents,
  isHoopBuilding,
  isFencing,
  isSolarPanels,
  isStorageGrain,
  isConfinementBuilding,
  isPoultryBuilding,
  isBarnOutbuilding,
  buildingClass,
  session,
  buildingAge = null,
  maxAgeThreshold = 15
}) => {
  // Business Rule (b), (d), (c): Dwelling/Contents, Hoop Buildings, Fencing always ACV only
  // These structure types do not qualify for Replacement Cost coverage under any circumstances.
  // Solar Panels join them — they are limit-based with no RC valuation. (#33790)
  if (isDwellingContents || isHoopBuilding || isFencing || isSolarPanels) {
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  // Business Rule (f): Silos/Grain Bins - Role-based access control
  // Underwriters can offer RC, Agents restricted to ACV only
  if (isStorageGrain) {
    return getRoleBasedLossSettlementOptions('storageGrain', session);
  }

  // Business Rule (a): Confinement Buildings - Age-based access control (#33612)
  // Buildings ≤ maxAgeThreshold years old: RC freely available to everyone (no approval).
  // Older/unknown age: RC requires UW approval — agents may still select it (shown as
  // "(Requires UW Approval)"), UW sees plain "Replacement cost". Same rule as Poultry.
  if (isConfinementBuilding) {
    if (buildingAge !== null && buildingAge <= maxAgeThreshold) {
      return [
        { id: 'ACV', value: 'Actual cash value' },
        { id: 'RC', value: 'Replacement cost' }
      ];
    }
    return getRoleBasedLossSettlementOptions('confinement', session);
  }

  // Business Rule (e): Poultry Buildings - Age-based access control with UW-approval fallback (#33612)
  // Buildings ≤ maxAgeThreshold years old: RC freely available to everyone.
  // Older/unknown age: RC requires UW approval (agent-selectable, UW sees plain RC).
  if (isPoultryBuilding) {
    if (buildingAge !== null && buildingAge <= maxAgeThreshold) {
      return [
        { id: 'ACV', value: 'Actual cash value' },
        { id: 'RC', value: 'Replacement cost' }
      ];
    }
    return getRoleBasedLossSettlementOptions('poultry', session);
  }

  // Business Rule (g): Barns/Outbuildings - Building Class determines coverage options
  // This logic overrides role-based access control for barn structures
  if (isBarnOutbuilding) {
    if (!buildingClass) {
      // Building class not yet determined - show both options to avoid blocking user
      // Once building characteristics are filled, proper restrictions will apply
      return [
        { id: 'ACV', value: 'Actual cash value' },
        { id: 'RC', value: 'Replacement cost' }
      ];
    }

    if (buildingClass === 1 || buildingClass === 2) {
      // Building Class 1 & 2: Must use RC (required for special Building Class 1 coverages)
      // For Building Class 1 & 2, spell out "Replacement Cost" in full (not abbreviated as "RC")
      return [{ id: 'RC', value: 'Replacement cost' }];
    }

    if (buildingClass === 3) {
      // Building Class 3: ACV only (older buildings not eligible for RC)
      return [{ id: 'ACV', value: 'Actual cash value' }];
    }

    // Fallback for invalid building class values
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  // Default for other structure types
  return [
    { id: 'RC', value: 'Replacement cost' },
    { id: 'ACV', value: 'Actual cash value' },
    { id: 'FUNC', value: 'Functional replacement cost' }
  ];
};

/**
 * Get roof loss settlement options for FO-6 structures
 *
 * GitHub issue #29142: Universal rule - Structure=RC → Roof=RC_ACV_WIND_HAIL, Structure=ACV → Roof=ACV
 * Unless exclusion/restriction area updates the roof loss settlement.
 *
 * @param {Object} params - Parameters object
 * @param {string} structureLossSettlement - Current structure loss settlement value
 * @param {number} buildingClass - Building class (1, 2, 3) for barn/outbuilding structures
 * @param {boolean} isBarnOutbuilding - Whether structure is barn/outbuilding type
 * @param {string} roofExclusionRestriction - Roof exclusion/restriction value ('excluded', 'restricted', or null)
 * @returns {Array} Available roof loss settlement options
 */
export const getRoofLossSettlementOptions = ({
  structureLossSettlement,
  buildingClass,
  isBarnOutbuilding,
  isConfinementBuilding,
  isPoultryBuilding,
  roofExclusionRestriction
}) => {
  // For barn outbuildings, prioritize building class logic over current structure settlement
  // This prevents timing issues during class transitions (e.g., 3→1 where buildingClass updates before structureLossSettlement)
  if (isBarnOutbuilding && buildingClass) {
    if (buildingClass === 3) {
      // Building Class 3 barns: ACV only (structure is always ACV for class 3)
      return [{ id: 'ACV', value: 'Actual cash value' }];
    }

    if (buildingClass === 1 || buildingClass === 2) {
      // GitHub issue #29142: Building Class 1 & 2 barns with RC structure default to RC_ACV_WIND_HAIL
      // (listed first, so it stays the default).
      // #33578: also offer plain "Replacement Cost" so an underwriter can upgrade the roof to full RC
      // (no ACV carveout for wind/hail). The RC option is gated to UW-only by the shared
      // roof-loss-settlement.jsx permission filter (Fo6Structures); agents only see RC_ACV_WIND_HAIL.
      // The rater maps both RC_ACV_WIND_HAIL and plain RC to ClarionDoor 'RC', so they rate identically.
      const options = [
        { id: 'RC_ACV_WIND_HAIL', value: 'Replacement cost with ACV for wind/hail' },
        { id: 'RC', value: 'Replacement Cost' }
      ];

      // Add "None" option when roof exclusion/restriction is configured
      // Use 'NONE' as the canonical value (matches backend post-data-rules)
      if (roofExclusionRestriction === 'excluded') {
        options.push({ id: 'NONE', value: 'Excluded' });
      }

      return options;
    }
  }

  // GitHub issue #29142: Structure=RC → Roof=RC_ACV_WIND_HAIL (the default, listed first).
  // Universal rule for all building types (confinement, poultry, silos, etc.)
  if (structureLossSettlement === 'RC') {
    const options = [{ id: 'RC_ACV_WIND_HAIL', value: 'Replacement cost with ACV for wind/hail' }];

    // #33612: Confinement & Poultry may also settle the roof at straight "Replacement Cost" — a
    // UW-only upgrade (full RC, no ACV carve-out for wind/hail), exactly like Type 1/2 barns (#33578).
    // The shared RoofLossSettlement component hides plain RC from non-UW users and the backend
    // downgrades it, so agents keep seeing only RC_ACV_WIND_HAIL. The rater maps both to CD 'RC'.
    if (isConfinementBuilding || isPoultryBuilding) {
      options.push({ id: 'RC', value: 'Replacement Cost' });
    }

    // Add Excluded option when roof exclusion/restriction is configured (#29139)
    if (roofExclusionRestriction === 'excluded') {
      options.push({ id: 'NONE', value: 'Excluded' });
    }
    return options;
  }

  // Structure=ACV or any other case → ACV only
  const options = [{ id: 'ACV', value: 'Actual cash value' }];
  if (roofExclusionRestriction === 'excluded') {
    options.push({ id: 'NONE', value: 'Excluded' });
  }
  return options;
};

/**
 * Validate roof loss settlement business rules
 * @param {Object} coverages - Coverage values object
 * @returns {Object} Validation result { isValid: boolean, message: string }
 */
export const validateRoofLossSettlement = (coverages) => {
  const { structureLossSettlement, roofLossSettlement, roofExclusionRestriction, hasRoofExclusionRestriction } =
    coverages || {};

  // NONE (roof excluded) is always valid
  if (roofLossSettlement === 'NONE') {
    return { isValid: true, message: '' };
  }

  // #33576: A "Roof Actual Cash Value" exclusion/restriction legitimately forces the roof loss
  // settlement to ACV even on an RC structure (e.g. Type 1/2 barns whose roof default is
  // RC_ACV_WIND_HAIL). Effect 10 in useFO6BusinessLogic sets roofLossSettlement='ACV' in that case,
  // so the RC → RC_ACV_WIND_HAIL rule below must not flag the resulting ACV roof as an error.
  if (hasRoofExclusionRestriction && roofExclusionRestriction === 'ACV' && roofLossSettlement === 'ACV') {
    return { isValid: true, message: '' };
  }

  // Rule: When structure is ACV, roof cannot be RC or RC_ACV_WIND_HAIL
  if (structureLossSettlement === 'ACV' && (roofLossSettlement === 'RC' || roofLossSettlement === 'RC_ACV_WIND_HAIL')) {
    return {
      isValid: false,
      message:
        'Roof loss settlement cannot be Replacement Cost (including RC with ACV for Wind/Hail) when structure loss settlement is ACV'
    };
  }

  // Rule: When structure is RC, the roof must settle at Replacement Cost — either RC_ACV_WIND_HAIL
  // (the default, ACV for wind/hail) or, since #33578, plain RC (full Replacement Cost, a UW-only
  // upgrade on Type 1/2 barns). Any other value (e.g. ACV) is invalid for an RC structure.
  if (structureLossSettlement === 'RC' && roofLossSettlement !== 'RC_ACV_WIND_HAIL' && roofLossSettlement !== 'RC') {
    return {
      isValid: false,
      message:
        'Roof loss settlement must be Replacement Cost (with or without ACV for Wind/Hail) when structure loss settlement is RC'
    };
  }

  return { isValid: true, message: '' };
};

/**
 * Roof exclusion/restriction dropdown options (#33576).
 *
 * A "Roof Actual Cash Value" restriction only makes sense when the roof loss settlement would
 * otherwise be Replacement Cost (RC_ACV_WIND_HAIL). For structures whose roof already settles at
 * ACV — ACV structures (Dwelling/Contents, Hoop, Fencing, Silos default, etc.) and Type 3 barns —
 * downgrading the roof to ACV is a no-op, so only "Roof Excluded" is offered.
 *
 * @param {Object} params
 * @param {boolean} params.isBarnOutbuilding - Whether the structure is a barn/outbuilding
 * @param {number} params.buildingClass - Barn building class (1, 2, 3)
 * @param {string} params.structureLossSettlement - Current structure loss settlement ('RC' | 'ACV')
 * @returns {Array<{id: string, value: string}>} Available roof exclusion/restriction options
 */
export const getRoofExclusionRestrictionOptions = ({ isBarnOutbuilding, buildingClass, structureLossSettlement }) => {
  // Mirror getRoofLossSettlementOptions' bucketing: the roof settles at Replacement Cost
  // (RC_ACV_WIND_HAIL) only for barn class 1/2 or an RC structure. Everything else — Type 3 barns,
  // ACV structures, and transient/undefined states (a barn before its class is chosen, an item
  // before structureLossSettlement is set) — already settles the roof at ACV, so the "Roof Actual
  // Cash Value" restriction is a no-op and only "Roof Excluded" is offered.
  const roofIsReplacementCost =
    (isBarnOutbuilding && (buildingClass === 1 || buildingClass === 2)) || structureLossSettlement === 'RC';

  const options = [{ id: 'excluded', value: 'Roof Excluded' }];
  if (roofIsReplacementCost) {
    options.push({ id: 'ACV', value: 'Roof Actual Cash Value' });
  }
  return options;
};

export const isAgent = (session) => {
  if (!session?.user) {
    return false; // Deny access if no session
  }

  const role = session.user.agencyRole?.toLowerCase();
  return role === 'agent';
};

/**
 * Calculate building age from year built
 * @param {string|number} yearBuilt - Year the building was built
 * @returns {number|null} Building age in years, or null if yearBuilt is invalid
 */
export const getConfinementBuildingAge = (yearBuilt) => {
  if (!yearBuilt) {
    return null;
  }

  const currentYear = new Date().getFullYear();
  const yearBuiltNum = parseInt(yearBuilt, 10);

  if (isNaN(yearBuiltNum) || yearBuiltNum < 1800 || yearBuiltNum > currentYear) {
    return null;
  }

  return currentYear - yearBuiltNum;
};

/**
 * Validates roof year against business rules
 * @param {string|number} roofYear - Roof year value
 * @param {string|number} yearBuilt - Building year built value
 * @returns {Object} Validation result with isValid and message
 */
export const validateRoofYear = (roofYear, yearBuilt) => {
  if (!roofYear) {
    return { isValid: true, message: '' };
  }

  const currentYear = new Date().getFullYear();
  const roofYearNum = parseInt(roofYear, 10);
  const yearBuiltNum = parseInt(yearBuilt, 10);

  // Check for future year
  if (roofYearNum > currentYear) {
    return {
      isValid: false,
      message: `Roof year cannot be in the future (max: ${currentYear})`
    };
  }

  // Check against year built
  if (yearBuilt && roofYearNum < yearBuiltNum) {
    return {
      isValid: false,
      message: `Roof year cannot be before building was built (${yearBuilt})`
    };
  }

  return { isValid: true, message: '' };
};

/**
 * #37180: the building fields to write when staff picks an FO-6 structure's roof shape. A structure saved
 * before the roof had separate shape and material selects holds its shape in roofType; picking a shape moves
 * the roof to the new fields, and the old roofType (a shape, not a material) is cleared.
 */
export const fo6RoofShapeWrites = (building, shape) =>
  holdsLegacyFo6RoofShape(building)
    ? [
        { field: 'roofShape', value: shape },
        { field: 'roofType', value: null }
      ]
    : [{ field: 'roofShape', value: shape }];

/**
 * #37180: the building fields to write when staff picks an FO-6 structure's roof material. On a structure
 * saved the old way the shape it holds in roofType moves to roofShape, unchanged, so the 360Value comparer
 * sees no shape change and doesn't re-run 360Value for a material pick.
 */
export const fo6RoofMaterialWrites = (building, material) =>
  holdsLegacyFo6RoofShape(building)
    ? [
        { field: 'roofShape', value: building.roofType },
        { field: 'roofType', value: material }
      ]
    : [{ field: 'roofType', value: material }];
